/**
 * randomUuid — the single id-minting helper for `apps/web` (BUG35).
 *
 * `crypto.randomUUID()` is unavailable on some Android WebViews (and JSDOM /
 * ancient engines). Called unguarded inside a mutation `onClick`, the throw
 * becomes an unhandled promise rejection and the user's action silently dies.
 * Every `apps/web` call site routes through this helper instead of touching
 * `crypto.randomUUID` directly; an ESLint rule keeps it that way.
 *
 * Three tiers, never throws:
 *   1. `crypto.randomUUID()` when it's a function.
 *   2. else an RFC-4122 v4 UUID built from `crypto.getRandomValues` — a real
 *      UUID shape, so any backend expecting UUID-formatted ids (e.g. the report
 *      `idempotencyKey`) still gets one.
 *   3. else a `Math.random`-based UUID-shaped string (last resort).
 *
 * `packages/shared` stays platform-free — this helper lives in `apps/web` and is
 * never imported into shared. Where shared injects an `IdFactory`, pass this.
 */

/** Format 16 bytes as a canonical 8-4-4-4-12 UUID string. */
function bytesToUuid(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < 16; i++) {
    const b = bytes[i] ?? 0;
    out += b.toString(16).padStart(2, '0');
    if (i === 3 || i === 5 || i === 7 || i === 9) out += '-';
  }
  return out;
}

/** Set the RFC-4122 version (4) and variant (10xx) bits in place. */
function applyV4Bits(bytes: Uint8Array): void {
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // version 4
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant 10
}

export function randomUuid(): string {
  // Tier 1 — the native API when it exists.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Tier 2 — build a v4 UUID from getRandomValues (far more widely available).
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    applyV4Bits(bytes);
    return bytesToUuid(bytes);
  }

  // Tier 3 — Math.random last resort (JSDOM / ancient engines without crypto).
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = Math.floor(Math.random() * 256);
  }
  applyV4Bits(bytes);
  return bytesToUuid(bytes);
}
